import { fopWithDescription } from "@/types/constants/formOfPayment";
import { GetInvoiceDetailsResponseObj } from "@/types/interfaces/crm";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import { UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import { formatInTorontoTime } from "../../utility/utils";

interface LedgerEntry {
  description?: string;
  date: Date | undefined;
  credit?: number;
  debit?: number;
  balance: number;
  formOfPayment?: number;
  accountNumber?: string;
  bankNumber?: string;
  authorizationNumber?: string;
}

interface Props {
  invoiceDetailsQueryClient: UseQueryResult;
}

function getFormOfPaymentDescription(entry: LedgerEntry): string {
  if (!entry.formOfPayment && entry.formOfPayment !== 0) {
    return "-";
  }

  let filtered = fopWithDescription.filter((fop) => fop.fop === entry.formOfPayment);

  const trimmedAccountNumber = entry.accountNumber?.trim();
  const entryAccountNumber = trimmedAccountNumber ? parseInt(trimmedAccountNumber) : null;
  const accountFiltered = filtered.filter((fop) => {
    const fopBankAccount = parseInt(fop.bankAccount || "") || null;
    return fopBankAccount === entryAccountNumber;
  });
  if (accountFiltered.length > 0) {
    filtered = accountFiltered;
  }

  const trimmedBankNumber = entry.bankNumber?.trim().toLowerCase();
  const entryBankNumber = trimmedBankNumber ? trimmedBankNumber : null;
  const bankFiltered = filtered.filter((fop) => {
    const fopBankNumber = (fop.bankNumber || "").trim().toLowerCase() || null;
    return fopBankNumber === entryBankNumber;
  });
  if (bankFiltered.length > 0) {
    filtered = bankFiltered;
  }

  if (filtered.length > 0) {
    return filtered[0].description;
  }
  return filtered.length > 0 ? filtered[0].description : "-";
}

export default function Ledger({ invoiceDetailsQueryClient }: Props) {
  const ledgerEntries: LedgerEntry[] = useMemo(() => {
    const data: GetInvoiceDetailsResponseObj =
      invoiceDetailsQueryClient?.data as unknown as GetInvoiceDetailsResponseObj;
    if (!data) return [];
    const entries: LedgerEntry[] = [];
    let balance = 0;

    for (let i = 0; i < data.data.reservations.length; i++) {
      const reservation = data.data.reservations[i];
      const supplier = `${parseInt(reservation.reservation.vendorNumber || "")} | ${
        reservation.reservation.vendorName
      } - ${reservation.reservation.ticketNumber || reservation.reservation.recordLocator || "N/A"}`;

      reservation.arInvoiceDetails.forEach((detail) => {
        balance += detail.grossAmount;
        entries.push({
          description: `AR - ${supplier}`,
          date: detail.systemDate,
          debit: detail.grossAmount,
          balance,
        });

        detail.payments.forEach((payment) => {
          balance -= payment.amount;
          entries.push({
            description: `AR - ${supplier}`,
            date: payment.systemDate,
            credit: payment.amount,
            balance,
            formOfPayment: payment.formOfPayment,
            accountNumber: payment.accountNumber,
          });
        });
      });

      reservation.ifInvoiceDetails.forEach((detail) => {
        balance += detail.grossAmount;
        entries.push({
          description: `IF - ${supplier}`,
          date: detail.transactionDate,
          debit: detail.grossAmount,
          balance,
        });

        detail.payments.forEach((payment) => {
          balance -= payment.amount;
          entries.push({
            description: `IF - ${supplier}`,
            date: detail.transactionDate,
            credit: payment.amount,
            balance,
            formOfPayment: payment.formOfPayment,
            accountNumber: payment.accountNumber,
            bankNumber: payment.bankNumber,
            authorizationNumber: payment.authorizationNumber,
          });
        });
      });
    }
    return entries;
  }, [invoiceDetailsQueryClient]);
  return (
    <>
      <Table
        variant="outlined"
        sx={{
          "& thead th": { textAlign: "center", border: "0.1px solid", borderColor: "divider" },
          "& tbody td": { textAlign: "center", border: "0.1px solid", borderColor: "divider" },
          "& thead th:nth-child(1)": { width: "5%" },
          "& thead th:nth-child(2)": { width: "11%" },
          "& thead th:nth-child(3)": { width: "30%" },
          "& thead th:nth-child(4)": { width: "14%" },
          "& thead th:nth-child(5)": { width: "12%" },
          "& thead th:nth-child(6)": { width: "15%" },
          "& thead th:nth-child(7)": { width: "10%" },
        }}
      >
        <thead>
          <tr>
            <th>No</th>
            <th>Date</th>
            <th>Description</th>
            <th>Invoice Amount</th>
            <th>Payment</th>
            <th>Form of Payment</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {ledgerEntries.map((entry, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{entry.date ? `${formatInTorontoTime(new Date(entry.date), "dd/MM/yyyy")}` : "-"}</td>
              <td>{entry.description || "-"}</td>
              <td
                style={{
                  textAlign: "right",
                }}
              >
                {entry.debit?.toFixed(2) ?? "-"}
              </td>
              <td
                style={{
                  textAlign: "right",
                }}
              >
                {entry.credit?.toFixed(2) ?? "-"}
              </td>
              <td>
                {entry.formOfPayment === 2 && entry.authorizationNumber ? (
                  <Tooltip title={`Auth Code: ${entry.authorizationNumber}`} placement="top" variant="outlined">
                    <span style={{ textDecoration: "underline", textDecorationStyle: "dotted", cursor: "help" }}>
                      {getFormOfPaymentDescription(entry)}
                    </span>
                  </Tooltip>
                ) : (
                  getFormOfPaymentDescription(entry)
                )}
              </td>
              <td
                style={{
                  textAlign: "right",
                }}
              >
                {entry.balance.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}
